import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import {
    ArrowLeftOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    UserOutlined,
    ArrowRightOutlined,
    EyeOutlined,
    ShareAltOutlined,
    HeartOutlined,
    TagsOutlined,
    ReadOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import { getImg } from '../../../Api/getImg';
import { useDoctorQuery } from '../../../hooks/useDoctorQuery';
import drEasyApi from '../../../Api/Customapi';
import { Image, Tag, Divider, Avatar, Tooltip } from 'antd';
import { nav } from 'framer-motion/client';
import MetaTags from '../../../context/MetaHelmet';
import SocialShare from '../../Buttons/SocialMediaButtons';
import { useLanguage } from '../../../context/LanguageContext';

export default function BlogDetails() {
    const navigate = useNavigate();
    const location = useLocation();
    const { slug, id } = useParams();
    const { data: doctor } = useDoctorQuery();
    const { t, currentLanguage } = useLanguage();
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [blog, setBlog] = useState(null);
    const [isLiked, setIsLiked] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [readingTime, setReadingTime] = useState(0);

    // Calculate reading time
    const calculateReadingTime = (content) => {
        const wordsPerMinute = 200;
        const words = content?.replace(/<[^>]+>/g, '').split(/\s+/).length || 0;
        return Math.ceil(words / wordsPerMinute);
    };

    // Fetch blog details
    useEffect(() => {
        const fetchBlogDetails = async () => {
            setLoading(true);
            try {
                const response = await drEasyApi.get(`/api/Blogs/getByID`, {
                    params: { Id: id }
                });
                const currentBlog = response.data;
                setBlog(currentBlog);
                setReadingTime(calculateReadingTime(currentBlog.content || currentBlog.description));
            } catch (err) {
                console.error('Failed to load blog details', err);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogDetails();
    }, [id,currentLanguage]);

    // Fetch related blogs
    useEffect(() => {
        const getBlogs = async () => {
            try {
                const res = await drEasyApi.get('/api/Blogs/GetDoctorBlogs', {
                    params: { DoctorId: doctor?.id },
                });
                const filtered = res.data?.filter(b => b.id !== id).slice(0, 4) || [];
                setRelatedBlogs(filtered);
            } catch (err) {
                console.error('Failed to fetch blogs', err);
            }
        };

        if (doctor?.id) {
            getBlogs();
        }
    }, [doctor?.id, id,currentLanguage]);

    // Function to detect if text contains Arabic characters
    const isArabic = (text) => {
        const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
        return arabicPattern.test(text);
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === "0001-01-01T00:00:00") {
            return new Date().toLocaleDateString(currentLanguage === 'ar' ? 'ar-EG' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
        const date = new Date(dateString);
        return date.toLocaleDateString(currentLanguage === 'ar' ? 'ar-EG' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateShort = (dateString) => {
        if (!dateString || dateString === "0001-01-01T00:00:00") {
            return new Date().toLocaleDateString(currentLanguage === 'ar' ? 'ar-EG' : 'en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        }
        const date = new Date(dateString);
        return date.toLocaleDateString(currentLanguage === 'ar' ? 'ar-EG' : 'en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleRelatedBlogClick = (relatedBlog) => {
        navigate(`/${slug}/blog/${relatedBlog.id}`, {
            state: {
                blog: {
                    id: relatedBlog.id,
                    title: relatedBlog.title,
                    image: relatedBlog.photoID,
                    description: relatedBlog.description,
                    date: relatedBlog.createdAt,
                    content: relatedBlog.content
                }
            }
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: blog.title,
                    text: blog.description,
                    url: window.location.href,
                });
            } catch (err) {
                console.log('Share cancelled');
            }
        } else {
            navigator.clipboard.writeText(window.location.href);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('blog.loading')}</p>
                </div>
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('blog.notFound')}</h2>
                    <p className="text-gray-600 mb-6">{t('blog.notFoundMessage')}</p>
                    <button
                        onClick={handleGoBack}
                        className="bg-primary hover:bg-secondary text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
                    >
                        {t('blog.goBack')}
                    </button>
                </div>
            </div>
        );
    }

    const hasArabicContent = isArabic(blog.title) || isArabic(blog.content || blog.description);
    const textAlignment = hasArabicContent ? 'text-right' : 'text-left';
    const direction = hasArabicContent ? 'rtl' : 'ltr';
    const currentUrl = window.location.href;
    const blogImage = 'https://res.cloudinary.com/dk2uh7sho/image/upload/v1752772138/dreasy_2_bsqyem.png';
    const blogDescription = blog?.description || t('blog.medicalArticle');
    const authorName = blog?.doctor?.name ? `${t('common.dr')} ${blog.doctor.name}` : t('blog.medicalProfessional');
    const blogTitle = blog?.title || t('blog.medicalArticle');

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            <div className="share-section mt-4">
                <MetaTags
                    title={blogTitle}
                    description={blogDescription}
                    image={blogImage}
                    url={currentUrl}
                    author={authorName}
                    publishedTime={blog.createdAt}
                />
            </div>

            <div className="mx-auto px-4 sm:px-6 lg:px-20 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    <div className="lg:col-span-3">
                        <article className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            {blog.photoID && (
                                <div className="relative">
                                    <Image
                                        src={getImg(blog.photoID)}
                                        alt={blog.title}
                                        className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                                        style={{ width: '100vw', height: '400px', objectFit: 'cover' }}
                                        onError={(e) => (e.target.src = 'https://via.placeholder.com/800x400?text=Blog+Image')}
                                    />
                                </div>
                            )}
                            <div className="p-6 sm:p-8 lg:p-10">
                                <div className='flex justify-between items-center mb-6'>
                                    <div className="border-b border-gray-200 pb-6 mb-8">
                                        <div className={`flex items-center gap-4 mb-6 `}>
                                            <Avatar
                                                size={60}
                                                src={blog.doctor?.photoID ? getImg(blog.doctor.photoID) : undefined}
                                                icon={!blog.doctor?.photoID && <UserOutlined />}
                                            />
                                            <div className={hasArabicContent ? 'text-right' : 'text-left'}>
                                                <h3 className="font-semibold text-lg text-gray-900">
                                                    {t('common.dr')} {blog.doctor?.name || t('blog.unknownAuthor')}
                                                </h3>
                                                <p className="text-gray-600">
                                                    {blog.doctor?.spicilaization || t('blog.medicalProfessional')}
                                                </p>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {t('blog.publishedOn', { date: formatDate(blog.createdAt) })}
                                                </p>
                                            </div>
                                        </div>

                                        <div className={`flex flex-wrap items-center gap-6 text-sm text-gray-600 `}>
                                            <div className="flex items-center gap-2">
                                                <CalendarOutlined className="text-primary" />
                                                <span>{formatDateShort(blog.createdAt)}</span>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <EyeOutlined className="text-primary" />
                                                <span>{t('blog.medicalArticle')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <SocialShare
                                        url={currentUrl}
                                        title={blog.title}
                                        description={blogDescription}
                                        image={blogImage}
                                    />
                                </div>
<h1 className='font-bold  text-xl text-primary mb-2'>{blog.title}</h1>

                                {/* Article Summary */}
                                {blog.description && (
                                    <div className={`mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl ${currentLanguage === 'ar' ? 'border-r-4 ' : 'border-l-4 '} border-primary `}>
                                        <p className="text-gray-700 text-lg leading-relaxed">
                                            {blog.description}
                                        </p>
                                    </div>
                                )}

                                <Divider />

                                {/* Article Footer */}
                                <div className={`mt-8 pt-6 `}>
                                    <div className="bg-gray-50 rounded-xl p-6">
                                        <h4 className="font-semibold text-gray-900 mb-3">{t('blog.aboutAuthor')}</h4>
                                        <Link to={`https://dreasey.com/${blog?.doctor?.slug}`} className={`flex items-center gap-4 `}>
                                            <Avatar
                                                size={48}
                                                src={blog.doctor?.photoID ? getImg(blog.doctor.photoID) : undefined}
                                                icon={!blog.doctor?.photoID && <UserOutlined />}
                                            />
                                            <div>
                                                <p className="font-medium text-gray-900">
                                                    {t('common.dr')} {blog.doctor?.name || t('blog.unknownAuthor')}
                                                </p>
                                                <p className="text-gray-600 text-sm">
                                                    {t('blog.specialistIn', { field: blog.doctor?.spicilaization || t('blog.generalMedicine') })}
                                                </p>
                                            </div>
                                        </Link>
                                    </div>

                                    <div className="mt-6 text-sm text-gray-500">
                                        <p>{t('blog.lastUpdated', { date: formatDate(blog.createdAt || new Date()) })}</p>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-8 space-y-6">
                            {/* Related Articles */}
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <ReadOutlined className="text-primary" />
                                    {t('blog.moreArticles')}
                                </h3>

                                {loading ? (
                                    <div className="space-y-4">
                                        {[...Array(3)].map((_, i) => (
                                            <div key={i} className="animate-pulse">
                                                <div className="h-16 bg-gray-200 rounded-lg mb-2"></div>
                                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                                                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                            </div>
                                        ))}
                                    </div>
                                ) : relatedBlogs.length > 0 ? (
                                    <div className="space-y-4">
                                        {relatedBlogs.map((relatedBlog) => (
                                            <div
                                                key={relatedBlog.id}
                                                className="group cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-all duration-200 border border-transparent hover:border-gray-200 hover:shadow-md"
                                                onClick={() => handleRelatedBlogClick(relatedBlog)}
                                            >
                                                <div className="flex gap-3 mb-2">
                                                    <img
                                                        src={getImg(relatedBlog.photoID)}
                                                        alt={relatedBlog.title}
                                                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                                        onError={(e) => (e.target.src = 'https://via.placeholder.com/64x64?text=Blog')}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-primary transition-colors">
                                                            {relatedBlog.title}
                                                        </h4>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {formatDateShort(relatedBlog.createdAt)}
                                                        </p>
                                                        <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <span className="text-xs text-primary font-medium">{t('blog.readMore')}</span>
                                                            <ArrowRightOutlined className="text-xs text-primary" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <hr></hr>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 text-sm">{t('blog.noRelatedArticles')}</p>
                                    </div>
                                )}

                                {/* View All Blogs Button */}
                                {relatedBlogs.length > 0 && (
                                    <div className="mt-6 pt-4">
                                        <button
                                            onClick={() => navigate(`/${slug}/blogs`)}
                                            className="w-full bg-primary hover:bg-secondary text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 text-sm flex items-center justify-center gap-2"
                                        >
                                            {t('blog.viewAllArticles')}
                                            <ArrowRightOutlined className="text-sm" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
                    color: #1f2937;
                    font-weight: 600;
                    line-height: 1.25;
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                }

                .prose p {
                    margin-bottom: 1.5rem;
                    line-height: 1.75;
                }

                .prose ul, .prose ol {
                    margin-bottom: 1.5rem;
                    padding-left: 2rem;
                }

                .prose li {
                    margin-bottom: 0.5rem;
                }

                .prose blockquote {
                    border-left: 4px solid #3b82f6;
                    background: #f8fafc;
                    padding: 1rem 1.5rem;
                    margin: 2rem 0;
                    border-radius: 0.5rem;
                    font-style: italic;
                    color: #475569;
                }

                .prose img {
                    border-radius: 0.75rem;
                    margin: 2rem 0;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
                }

                .prose a {
                    color: #3b82f6;
                    text-decoration: underline;
                    transition: color 0.2s;
                }

                .prose a:hover {
                    color: #1d4ed8;
                }

                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: .5; }
                }

                .animate-pulse {
                    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .animate-spin {
                    animation: spin 1s linear infinite;
                }
            `}</style>
        </div>
    );
}